import React, { useEffect, useState } from "react";
import {
  Alert,
  Avatar,
  Box,
  Button,
  CircularProgress,
  Paper,
  TextField,
  Typography,
} from "@mui/material";
import axios from "axios";
import moment from "moment";
import { useParams } from "react-router-dom";
import { isValidEmail, isValidUrl } from "util/validators";

const containerSx = { maxWidth: 700, mx: "auto", px: 2, py: 4 };
const detailItemSx = { flex: "1 1 45%", minWidth: 120 };
const formFieldSx = { flex: "1 1 45%", minWidth: 200 };

function LogoHeader() {
  return (
    <Box sx={{ textAlign: "center", py: 2, mb: 2 }}>
      <img src="/images/logo.png" style={{ height: 45 }} alt="DojoMojo" />
    </Box>
  );
}

function DetailItem({ label, value }) {
  return (
    <Box sx={detailItemSx}>
      <Typography variant="caption" color="text.secondary">
        {label}
      </Typography>
      <Typography variant="body2">{value}</Typography>
    </Box>
  );
}

function PartnerChip({ partner }) {
  return (
    <Box
      sx={{
        display: "flex",
        alignItems: "center",
        gap: 0.5,
        bgcolor: "white",
        px: 1.5,
        py: 0.5,
        borderRadius: 4,
        border: "1px solid",
        borderColor: "grey.300",
      }}
    >
      <Avatar src={partner.logo} alt={partner.name} sx={{ width: 20, height: 20 }} />
      <Typography variant="caption">{partner.name}</Typography>
    </Box>
  );
}

/**
 * Public page for external brands to accept campaign invitations
 * @returns {import("react").ReactElement}
 */
export default function ExternalBrandAcceptPage() {
  const { code } = useParams();

  const [loading, setLoading] = useState(true);
  const [invite, setInvite] = useState(null);
  const [error, setError] = useState(null);
  const [formData, setFormData] = useState({
    brandName: "",
    brandUrl: "",
    fullName: "",
    email: "",
  });
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState(null);

  useEffect(() => {
    loadInvite();
  }, [code]);

  const loadInvite = async () => {
    try {
      const response = await axios.get(`/api/external-brand-invite/${code}`);

      if (!response.data.success) {
        setError(response.data.message);
        setLoading(false);
        return;
      }

      const inviteData = response.data.invite;
      setInvite(inviteData);
      setFormData({
        brandName: inviteData.brandName ?? "",
        brandUrl: inviteData.brandUrl ?? "",
        fullName: inviteData.fullName ?? "",
        email: inviteData.brandEmail ?? "",
      });
      setLoading(false);
    } catch (err) {
      setError("Failed to load invitation");
      setLoading(false);
    }
  };

  const handleFieldChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async () => {
    if (!formData.brandName.trim()) {
      setSubmitError("Brand name is required");
      return;
    }
    if (!formData.email.trim()) {
      setSubmitError("Email is required");
      return;
    }
    if (!isValidEmail(formData.email.trim())) {
      setSubmitError("Please enter a valid email address");
      return;
    }
    if (formData.brandUrl.trim() && !isValidUrl(formData.brandUrl.trim())) {
      setSubmitError("Please enter a valid website URL");
      return;
    }

    setSubmitting(true);
    setSubmitError(null);

    try {
      const response = await axios.post(`/api/external-brand-invite/${code}/accept`, {
        brandName: formData.brandName.trim(),
        brandUrl: formData.brandUrl.trim(),
        fullName: formData.fullName.trim(),
        email: formData.email.trim(),
      });

      if (response.data.success) {
        window.location.href = response.data.redirectUrl;
      }
    } catch (err) {
      setSubmitError(err.response?.data?.message || "Failed to accept invitation");
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <Box sx={containerSx}>
        <LogoHeader />
        <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
          <CircularProgress />
        </Box>
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={containerSx}>
        <LogoHeader />
        <Paper sx={{ p: 4, borderRadius: 2 }}>
          <Alert severity="error">{error}</Alert>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 2, textAlign: "center" }}>
            If you believe this is an error, please contact{" "}
            <a href="mailto:hello@dojomojo.com">hello@dojomojo.com</a>
          </Typography>
        </Paper>
      </Box>
    );
  }

  const campaign = invite.campaign ?? {};
  const hostBrand = invite.hostBrand ?? {};
  const inviter = invite.inviter ?? {};
  const confirmedPartners = campaign.confirmedPartners ?? [];

  const detailItems = [
    { label: "Start Date", value: moment(campaign.startDate).format("MMM D, YYYY") },
    { label: "End Date", value: moment(campaign.endDate).format("MMM D, YYYY") },
    campaign.prizing && { label: "Prize", value: campaign.prizing },
    campaign.prizingValue && { label: "Est. Prize Value", value: `$${campaign.prizingValue}` },
  ].filter(Boolean);

  const formFields = [
    { field: "brandName", label: "Brand Name", required: true },
    { field: "fullName", label: "Your Name" },
    { field: "email", label: "Email", type: "email", required: true },
    { field: "brandUrl", label: "Brand Website", placeholder: "https://yourbrand.com" },
  ];

  return (
    <Box sx={containerSx}>
      <LogoHeader />

      <Paper sx={{ p: 4, borderRadius: 2 }}>
        {/* Header */}
        <Box sx={{ textAlign: "center", mb: 3 }}>
          <Typography variant="h2" gutterBottom>
            You&apos;re Invited!
          </Typography>
          <Typography variant="body1" color="text.secondary">
            <strong>{inviter?.name || hostBrand.name}</strong> has invited{" "}
            <strong>{invite.brandName}</strong> to join their upcoming giveaway as a brand partner!
          </Typography>
        </Box>

        {/* Campaign Card */}
        <Paper variant="outlined" sx={{ p: 3, mb: 3, bgcolor: "grey.50", borderRadius: 2 }}>
          <Typography variant="h6" sx={{ mb: 2 }}>
            Campaign Details
          </Typography>

          <Box sx={{ display: "flex", alignItems: "center", gap: 2, mb: 2 }}>
            {hostBrand.logo && (
              <Avatar src={hostBrand.logo} alt={hostBrand.name} sx={{ width: 48, height: 48 }} />
            )}
            <Box>
              <Typography variant="subtitle1" fontWeight="bold">
                {campaign.name}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                Hosted by {hostBrand.name}
              </Typography>
            </Box>
          </Box>

          <Box sx={{ display: "flex", flexWrap: "wrap", gap: 2 }}>
            {detailItems.map((item) => (
              <DetailItem key={item.label} label={item.label} value={item.value} />
            ))}
          </Box>

          {campaign.description && (
            <Box sx={{ mt: 2 }}>
              <Typography variant="caption" color="text.secondary">
                Description
              </Typography>
              <Typography variant="body2">{campaign.description}</Typography>
            </Box>
          )}

          {confirmedPartners.length > 0 && (
            <Box sx={{ mt: 2 }}>
              <Typography variant="caption" color="text.secondary">
                Confirmed Partners
              </Typography>
              <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1, mt: 1 }}>
                {confirmedPartners.map((partner) => (
                  <PartnerChip key={partner.id} partner={partner} />
                ))}
              </Box>
            </Box>
          )}
        </Paper>

        {/* Form Section */}
        <Box sx={{ mb: 3 }}>
          <Typography variant="h6" sx={{ mb: 1 }}>
            Confirm Your Details
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Please verify or update your information below to join this campaign.
          </Typography>

          {submitError && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {submitError}
            </Alert>
          )}

          <Box sx={{ display: "flex", flexWrap: "wrap", gap: 2 }}>
            {formFields.map(({ field, label, type, required, placeholder }) => (
              <Box key={field} sx={formFieldSx}>
                <TextField
                  name={field}
                  label={label}
                  type={type}
                  value={formData[field] ?? ""}
                  onChange={handleFieldChange}
                  fullWidth
                  required={required}
                  placeholder={placeholder}
                />
              </Box>
            ))}
          </Box>
        </Box>

        {/* Info Box */}
        <Alert severity="info" sx={{ mb: 3 }}>
          By accepting, you&apos;ll create a DojoMojo account with a{" "}
          <strong>30-day free trial</strong> and automatically join this campaign as a partner.
        </Alert>

        {/* Submit Button */}
        <Box sx={{ textAlign: "center" }}>
          <Button
            variant="contained"
            color="primary"
            size="large"
            onClick={handleSubmit}
            disabled={submitting}
            sx={{ minWidth: 280, py: 1.5 }}
            startIcon={submitting ? <CircularProgress size={20} color="inherit" /> : null}
          >
            {submitting ? "Creating Account..." : "Accept Invitation & Join Campaign"}
          </Button>
        </Box>

        {/* Footer */}
        <Typography variant="body2" color="text.secondary" sx={{ mt: 3, textAlign: "center" }}>
          Questions? Contact us at{" "}
          <a href="mailto:hello@dojomojo.com" style={{ color: "#1976d2" }}>
            hello@dojomojo.com
          </a>
        </Typography>
      </Paper>

      {/* About DojoMojo */}
      <Typography
        variant="caption"
        color="text.secondary"
        sx={{ display: "block", mt: 3, textAlign: "center", px: 2 }}
      >
        <strong>DojoMojo</strong> is a private, invite-only network that helps lifestyle brands grow
        their most valuable audiences through authentic brand partnerships.
      </Typography>
    </Box>
  );
}
